import React from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useChats } from '../../contexts/ChatsContext';
import { formatDateTime } from '../../utils/dateUtils';

export interface Chat {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

const ChatList: React.FC = () => {
  const { t } = useTranslation();
  const {
    chats,
    currentChatId,
    isLoading,
    handleNewChat,
    handleChatSelect,
    handleRenameChat,
    handleDeleteChat,
  } = useChats();

  const promptRename = (chatId: string) => {
    const chat = chats.find((c) => c.id === chatId);
    if (!chat) return;

    const newName = prompt(t('chat.enter_new_name'), chat.name);
    if (newName !== null) {
      handleRenameChat(chatId, newName);
    }
  };

  const confirmDelete = (chatId: string) => {
    if (window.confirm(t('chat.confirm_delete'))) {
      handleDeleteChat(chatId);
    }
  };

  return (
    <div className="w-64 border-r flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        {isLoading && <div className="text-center text-gray-500 mt-4">Loading...</div>}
        {!isLoading && chats.length === 0 && (
          <div className="text-center text-gray-500 mt-4">No chats found</div>
        )}
        {!isLoading &&
          chats.map((chat) => (
            <div
              key={chat.id}
              className={`p-3 border-b cursor-pointer hover:bg-gray-200 flex justify-between items-center ${
                currentChatId === chat.id ? 'bg-gray-200' : ''
              }`}
              onClick={() => handleChatSelect(chat.id)}
            >
              <div className="truncate flex-1">
                <div>{chat.name}</div>
                <div className="text-sm text-gray-500">{formatDateTime(chat.updated_at)}</div>
              </div>
              <div className="flex gap-2 ml-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    promptRename(chat.id);
                  }}
                  className="text-gray-600 hover:text-blue-600"
                  title={t('common.rename')}
                >
                  <FaEdit className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmDelete(chat.id);
                  }}
                  className="text-gray-600 hover:text-red-600"
                  title={t('common.delete')}
                >
                  <FaTrash className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
      </div>
      <div className="p-4">
        <button
          onClick={handleNewChat}
          className="w-full py-2 px-4 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          {t('chat.new_chat')}
        </button>
      </div>
    </div>
  );
};

export default ChatList;
